import { computed, useHost, type ComputedRef } from "vue";
import type { EditorHeaderProps } from "./editor.types";
import { useSharedState } from "composables/useSharedState";
import { createEventDispatcher } from "@/runtime/shared/events";

// Constants

const LANGUAGE_ICON_MAP: Record<string, string> = {
  html: "file-html",
  xml: "file-xml",
  json: "file-json",
  css: "file-css",
  javascript: "file-javascript",
  typescript: "file-javascript",
  markdown: "file-outline",
};

const LANGUAGE_LABEL_MAP: Record<string, string> = {
  html: "HTML",
  xml: "XML",
  json: "JSON",
  css: "CSS",
  scss: "SCSS",
  less: "LESS",
  javascript: "JavaScript",
  typescript: "TypeScript",
  markdown: "Markdown",
};

// Types

export interface UseEditorHeaderOptions {
  props: Readonly<EditorHeaderProps>;
}

export interface UseEditorHeaderReturn {
  resolvedLanguage: ComputedRef<string>;
  languageIconName: ComputedRef<string>;
  languageLabel: ComputedRef<string>;
  handleCopy: () => void;
}

// Composable

export const useEditorHeader = (options: UseEditorHeaderOptions): UseEditorHeaderReturn => {
  const { props } = options;

  // State setup
  const state = useSharedState(props);
  const host = useHost();
  const dispatchEvent = createEventDispatcher(host);

  // Computed refs
  const resolvedLanguage = computed(() => state.language?.value ?? props.language ?? "javascript");
  const languageIconName = computed(() => LANGUAGE_ICON_MAP[resolvedLanguage.value] ?? "code");
  const languageLabel = computed(() => LANGUAGE_LABEL_MAP[resolvedLanguage.value] ?? resolvedLanguage.value);

  // Methods
  const handleCopy = () => {
    dispatchEvent("editor-copy");
  };

  return {
    resolvedLanguage,
    languageIconName,
    languageLabel,
    handleCopy,
  };
};
